<template>
  <div>
    <v-btn color="primary" dark @click="openDialog" class="text-capitalize">
      <v-icon left>mdi-plus</v-icon>
      Add Template
    </v-btn>

    <v-dialog v-model="dialog" max-width="700" persistent>
      <v-card>
      <v-card-title class="text-h6 primary white--text">
        {{ isEditMode ? 'Update Template' : 'Add New Template' }}
      </v-card-title>

      <v-card-text class="mt-6">
        <v-form ref="form" v-model="valid">
          <v-row>
            <v-col cols="12">
              <v-select
                v-model="formData.eventName"
                :items="availableEvents"
                label="Event Name *"
                :rules="[requiredValidator('Event Name')]"
                :disabled="isEditMode"
                dense
                solo
              ></v-select>
            </v-col>

            <v-col cols="12">
              <v-autocomplete
                v-model="formData.templateName"
                :items="gallaboxTemplates"
                :loading="templatesLoading"
                :search-input.sync="templateSearch"
                item-text="name"
                item-value="name"
                label="Template Name *"
                :rules="[requiredValidator('Template Name')]"
                dense
                solo
                placeholder="Search template name from Gallabox"
                no-data-text="No templates found"
                :filter="filterTemplates"
                @change="onTemplateSelected"
                clearable
              >
                <template v-slot:item="{ item }">
                  <v-list-item-content>
                    <v-list-item-title>{{ item.name }}</v-list-item-title>
                    <v-list-item-subtitle>
                      Variables: {{ item.variableCount }} | Status: {{ item.status }}
                    </v-list-item-subtitle>
                  </v-list-item-content>
                </template>
              </v-autocomplete>
            </v-col>

            <v-col cols="12">
              <v-textarea
                v-model="formData.description"
                label="Description"
                rows="2"
                dense
                solo
              ></v-textarea>
            </v-col>

            <v-col cols="12">
              <v-switch
                v-model="formData.active"
                :input-value="formData.active"
                label="Active"
                color="success"
                inset
                dense
              >
                <template v-slot:label>
                  <span :class="formData.active ? 'success--text' : 'grey--text'">
                    {{ formData.active ? 'Active' : 'Inactive' }}
                  </span>
                </template>
              </v-switch>
            </v-col>

            <v-col cols="12" sm="6">
              <v-text-field
                v-model.number="formData.variableCount"
                label="Number of Variables"
                type="number"
                min="0"
                dense
                solo
                readonly
                hint="Auto-filled from template"
                persistent-hint
              ></v-text-field>
            </v-col>

            <v-col cols="12">
              <v-text-field
                v-model="variableNamesInput"
                label="Variable Names (comma separated)"
                dense
                solo
                :placeholder="variablePlaceholder"
                hint="Auto-filled from template"
                persistent-hint
                readonly
              ></v-text-field>
            </v-col>
          </v-row>
        </v-form>
      </v-card-text>

      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn text @click="closeDialog">Cancel</v-btn>
        <v-btn
          color="primary"
          :loading="btnLoading"
          @click="saveTemplate"
          :disabled="!valid"
        >
          {{ isEditMode ? 'Update' : 'Save' }}
        </v-btn>
      </v-card-actions>
    </v-card>

      <v-snackbar v-model="snackbar" :color="snackbarColor" timeout="3000" top right>
        {{ snackbarText }}
      </v-snackbar>
    </v-dialog>
  </div>
</template>

<script>
import { requiredValidator } from "../../core/methods/validations";
import getAllTemplatesFromGallabox from '../api/getAllTemplatesFromGallabox.graphql';

export default {
  props: {
    editTemplate: {
      type: Object,
      default: null
    },
    usedEventNames: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      requiredValidator,
      dialog: false,
      valid: false,
      btnLoading: false,
      snackbar: false,
      snackbarText: '',
      snackbarColor: 'success',
      variableNamesInput: '',
      templateSearch: '',
      gallaboxTemplates: [],
      templatesLoading: false,
      allEventNames: [
        'next_day_after_start_date',
        'Fifth_day_after_start_date',
        'Three_days_before_expiry',
        'six_days_after_expiry',
        'twelve_days_after_expiry',
        'first_subscription_plan',
        'second_subscription_onwards',
        'after_purchase_from_shop',
        'adding_new_product_to_shop',
        'payment_invoice',
        'plan_update',
        'twelve_day_after_start_date',
        'three_days_after_expiry'
      ],
      formData: {
        eventName: '',
        templateName: '',
        description: '',
        variableCount: 0,
        variables: [],
        active: true
      }
    };
  },
  computed: {
    isEditMode() {
      return this.editTemplate !== null;
    },
    availableEvents() {
      if (this.isEditMode) {
        return [this.editTemplate.eventName];
      }
      return this.allEventNames.filter(event => !this.usedEventNames.includes(event));
    },
    variablePlaceholder() {
      return 'e.g. ' + '{{name}}, {{phone}}, {{amount}}';
    }
  },
  watch: {
    editTemplate: {
      handler(newVal) {
        if (newVal) {
          console.log('Edit template - incoming active value:', newVal.active);
          const activeValue = newVal.active === true;
          console.log('Edit template - computed active value:', activeValue);

          this.formData = {
            _id: newVal._id,
            eventName: newVal.eventName,
            templateName: newVal.templateName,
            description: newVal.description || '',
            variableCount: newVal.variableCount || 0,
            variables: newVal.variables || [],
            active: activeValue
          };
          // Set variable names input from array
          this.variableNamesInput = (newVal.variables || []).join(', ');
          this.dialog = true;

          console.log('Edit template - formData.active after set:', this.formData.active);
        }
      },
      immediate: true,
      deep: true
    },
    'formData.active': {
      handler(newVal) {
        console.log('formData.active changed to:', newVal);
      }
    }
  },
  methods: {
    async openDialog() {
      this.dialog = true;
      await this.fetchGallaboxTemplates();
    },
    async fetchGallaboxTemplates() {
      this.templatesLoading = true;
      try {
        const result = await this.$apollo.query({
          query: getAllTemplatesFromGallabox,
          fetchPolicy: 'network-only'
        });

        if (result.data?.getAllTemplatesFromGallabox?.success) {
          this.gallaboxTemplates = result.data.getAllTemplatesFromGallabox.templates || [];
        } else {
          this.snackbarText = result.data?.getAllTemplatesFromGallabox?.message || 'Failed to fetch templates';
          this.snackbarColor = 'error';
          this.snackbar = true;
        }
      } catch (error) {
        console.error('Error fetching Gallabox templates:', error);
        this.snackbarText = 'Failed to fetch templates from Gallabox';
        this.snackbarColor = 'error';
        this.snackbar = true;
      } finally {
        this.templatesLoading = false;
      }
    },
    filterTemplates(item, queryText) {
      const name = item.name?.toLowerCase() || '';
      const search = queryText?.toLowerCase() || '';
      return name.includes(search);
    },
    onTemplateSelected(templateName) {
      if (!templateName) {
        this.formData.variableCount = 0;
        this.variableNamesInput = '';
        return;
      }

      // Find the selected template from the list
      const selectedTemplate = this.gallaboxTemplates.find(t => t.name === templateName);
      if (selectedTemplate) {
        this.formData.variableCount = selectedTemplate.variableCount || 0;
        this.variableNamesInput = (selectedTemplate.variables || []).join(', ');
        this.formData.variables = selectedTemplate.variables || [];
      }
    },
    saveTemplate() {
      if (!this.$refs.form.validate()) {
        return;
      }

      this.btnLoading = true;
      const mutation = this.isEditMode
        ? require('../api/updateGallaboxTemplate.graphql')
        : require('../api/createGallaboxTemplate.graphql');

      const variables = this.isEditMode
        ? { id: this.formData._id, input: this.getInputData() }
        : { input: this.getInputData() };

      this.$apollo
        .mutate({
          mutation,
          variables
        })
        .then(() => {
          this.snackbarText = `Template ${this.isEditMode ? 'updated' : 'created'} successfully`;
          this.snackbarColor = 'success';
          this.snackbar = true;
          this.btnLoading = false;
          this.closeDialog();
          this.$emit('template-saved');
        })
        .catch(error => {
          this.snackbarText = error.message || `Failed to ${this.isEditMode ? 'update' : 'create'} template`;
          this.snackbarColor = 'error';
          this.snackbar = true;
          this.btnLoading = false;
        });
    },
    getInputData() {
      // Parse variables from comma-separated input
      const variablesArray = this.variableNamesInput
        ? this.variableNamesInput.split(',').map(v => v.trim()).filter(v => v)
        : [];

      // Explicitly handle active as boolean - ensure it's true or false
      const isActive = this.formData.active === true;

      console.log('getInputData - active value:', this.formData.active, 'isActive:', isActive);

      return {
        eventName: this.formData.eventName,
        templateName: this.formData.templateName,
        description: this.formData.description || '',
        variableCount: this.formData.variableCount || 0,
        variables: variablesArray,
        active: isActive
      };
    },
    closeDialog() {
      this.dialog = false;
      this.$refs.form.reset();
      this.variableNamesInput = '';
      this.templateSearch = '';
      this.formData = {
        eventName: '',
        templateName: '',
        description: '',
        variableCount: 0,
        variables: [],
        active: true
      };
      this.$emit('close');
    }
  }
};
</script>

